import React, {memo} from 'react';
import Dialog from 'components/Dialog';
import FormikTextField from 'components/inputs/FormikTextField';
import {FormControlLabel, Grid, Radio, RadioGroup, Typography} from '@mui/material';
import {FormFooterButtons} from 'components/buttons/FormFooterButtons';
import * as yup from 'common/yup';
import Formik, {FormOnSubmitHelpers} from 'components/hook-form/Formik';
import ReactFlagsSelectForm from 'components/inputs/ReactFlagsSelectForm';
import {ISearchCriteria, SearchMethod} from 'components/forms/SearchSchema';
import {useFormContext} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useFieldValue} from 'components/hook-form/useFieldValue';
import {ipString} from 'common/yup/customs/ip';

export const IPCountryNames = {
   name: 'name',
   countryCode: 'countryCode',
   method: '_method',
} as const;

const _validationSchema = yup.object({
   [IPCountryNames.name]: ipString().when([IPCountryNames.method], {
      is: (method) => method === SearchMethod.field,
      then: ipString().required(),
   }),
   [IPCountryNames.countryCode]: yup.string().when([IPCountryNames.method], {
      is: (method) => method === SearchMethod.unused,
      then: yup.string().required(),
   }),
   [IPCountryNames.method]: yup.string().required().default(SearchMethod.field),
});
export type IPCountryFormType = yup.InferType<typeof _validationSchema>;

export interface IPCountryDialogProps {
   title: string;
   label: string;
   type?: 'text' | 'password';
   open?: boolean;
   value?: string;
   validationSchema?: typeof _validationSchema;
   onClose: (
      nameObj?: IPCountryFormType,
      callbacks?: FormOnSubmitHelpers<IPCountryFormType>
   ) => void;
}
export const IPCountryDialog = memo(
   ({
      title,
      value,
      onClose: onSubmit,
      label,
      type = 'text',
      open = true,
      validationSchema = _validationSchema,
   }: IPCountryDialogProps) => {
      const onClose = React.useCallback(() => onSubmit(), [onSubmit]);
      return (
         <Dialog
            open={open}
            maxWidth="sm"
            header={title}
            close={onClose}
            sx={{
               '& .MuiPaper-root.MuiDialog-paper, & .MuiDialogContent-root': {
                  overflowY: 'unset',
               },
            }}
         >
            <Formik
               initialValues={validationSchema.cast({name: value})}
               initialTouched={Boolean(value) ? ['name'] : undefined}
               validationSchema={validationSchema}
               onSubmit={onSubmit}
            >
               <IPCountryForm cancel={onClose} label={label} type={type} />
            </Formik>
         </Dialog>
      );
   }
);

const IPCountryForm = ({
   label,
   cancel,
   type,
}: {
   label: string;
   cancel: () => void;
   type: IPCountryDialogProps['type'];
}) => {
   const {t} = useTranslation();
   const {setValue} = useFormContext<IPCountryFormType>();
   const method = useFieldValue<IPCountryFormType>(IPCountryNames.method);
   console.debug('method', method);
   return (
      <>
         <RadioGroup
            name={IPCountryNames.method}
            onChange={(event) => {
               setValue(IPCountryNames.method, event.target.value as ISearchCriteria['method']);
            }}
         >
            <Grid container spacing={3} justifyContent="center">
               <Grid item xs={4}>
                  <FormControlLabel
                     checked={method === SearchMethod.field}
                     value={SearchMethod.field}
                     control={<Radio />}
                     label={<Typography variant={'body2'}>{t('IP Address')}</Typography>}
                  />
               </Grid>
               <Grid item sm={8}>
                  <FormikTextField
                     autoFocus
                     type={type}
                     name="name"
                     label={label}
                     disabled={method !== SearchMethod.field}
                  />
               </Grid>

               <Grid item xs={4}>
                  <FormControlLabel
                     checked={method === SearchMethod.unused}
                     value={SearchMethod.unused}
                     control={<Radio />}
                     label={<Typography variant={'body2'}>{t('Country')}</Typography>}
                  />
               </Grid>
               <Grid item sm={8}>
                  <ReactFlagsSelectForm
                     name={IPCountryNames.countryCode}
                     searchable
                     disabled={method !== SearchMethod.unused}
                  />
               </Grid>
            </Grid>
         </RadioGroup>
         <Grid container spacing={3} justifyContent="center">
            <Grid item>
               <FormFooterButtons cancel={cancel} />
            </Grid>
         </Grid>
      </>
   );
};
